import React, { memo } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ContentPasteOutlined,
  DescriptionOutlined,
  KeyboardArrowDown,
  KeyboardArrowUp,
  MoreHoriz,
  Science,
  SmartToyOutlined,
  VideoCall,
} from '@mui/icons-material';
import { formatDate } from '../../utils/dateUtils';
import { IconTile } from '../pagekit';
import TranscriptView from './TranscriptView';
import { getSourceLabel, parseExtractionTypeLabels } from './transcriptUtils';

/**
 * Icon for a session's origin.
 * @param {object} session - Session.
 * @returns {JSX.Element} Icon.
 */
function sourceIcon(session) {
  if (session.source === 'fathom') return <VideoCall />;
  if (session.source === 'recall') return <SmartToyOutlined />;
  if (session.contentType === 'paste') return <ContentPasteOutlined />;
  return <DescriptionOutlined />;
}

/**
 * One session in the project's session list.
 *
 * @param {object} props
 * @param {object} props.session - Session.
 * @param {number} props.requirementCount - Requirements extracted from it.
 * @param {boolean} props.isExtracting - An extraction job is running for it.
 * @param {boolean} props.expanded - Transcript preview open.
 * @param {boolean} props.loadingTranscript - Transcript is loading.
 * @param {object|null} props.transcript - Normalised transcript (when loaded).
 * @param {Function} props.onOpen - Open the session hub.
 * @param {Function} props.onToggleExpand - Toggle the preview.
 * @param {Function} [props.onExtract] - Start an extraction (shown when nothing was extracted).
 * @param {Function} props.onMenu - (event) => open the actions menu.
 * @param {Function} [props.onViewRequirements] - Jump to its requirements.
 * @returns {JSX.Element}
 */
function SessionRow({
  session,
  requirementCount,
  isExtracting,
  expanded,
  loadingTranscript,
  transcript,
  onOpen,
  onToggleExpand,
  onExtract,
  onMenu,
  onViewRequirements,
}) {
  const typeLabels = parseExtractionTypeLabels(session.extractionType);
  const meta = [
    getSourceLabel(session.source),
    session.file?.name,
    session.contentType === 'paste' && !session.file ? 'Pasted text' : null,
    session.createdAt ? formatDate(session.createdAt) : null,
  ].filter(Boolean);

  let status;
  if (isExtracting) {
    status = <Chip size="small" color="primary" icon={<CircularProgress size={12} color="inherit" />} label="Extracting" />;
  } else if (requirementCount > 0) {
    status = (
      <Chip
        size="small"
        color="success"
        label={`${requirementCount} requirement${requirementCount === 1 ? '' : 's'}`}
        clickable={Boolean(onViewRequirements)}
        onClick={onViewRequirements}
      />
    );
  } else {
    status = <Chip size="small" color="warning" label="Not extracted" />;
  }

  return (
    <Box
      component="li"
      sx={{
        listStyle: 'none',
        borderBottom: 1,
        borderColor: 'var(--q-row-divider)',
        '&:last-of-type': { borderBottom: 0 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          gap: 1.5,
          px: { xs: 1.5, sm: 2 },
          py: 1.5,
          '&:hover': { bgcolor: 'var(--q-bg-subtle)' },
        }}
      >
        <IconTile tone={session.source === 'fathom' || session.source === 'recall' ? 'accent' : 'neutral'} size={36}>
          {sourceIcon(session)}
        </IconTile>

        <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
          <Box
            component="button"
            type="button"
            onClick={onOpen}
            sx={{
              all: 'unset',
              cursor: 'pointer',
              display: 'block',
              maxWidth: '100%',
              borderRadius: 1,
              '&:hover .q-session-name': { textDecoration: 'underline' },
              '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
            }}
          >
            <Typography className="q-session-name" variant="body2" sx={{ fontWeight: 600 }} noWrap>
              {session.name || 'Untitled session'}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {meta.join(' · ')}
          </Typography>
          {session.description && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {session.description}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 0.75,
            justifyContent: { sm: 'flex-end' },
            pl: { xs: 6, sm: 0 },
            flex: { xs: '1 1 100%', sm: '0 0 auto' },
          }}
        >
          {typeLabels.slice(0, 2).map((label) => (
            <Chip key={label} label={label} size="small" variant="outlined" sx={{ display: { xs: 'none', md: 'inline-flex' } }} />
          ))}
          {status}
          {!isExtracting && requirementCount === 0 && onExtract && (
            <Button size="small" variant="outlined" startIcon={<Science />} onClick={onExtract}>
              Extract
            </Button>
          )}
          <Tooltip title={expanded ? 'Hide transcript' : 'Preview transcript'}>
            <span>
              <IconButton
                size="small"
                onClick={onToggleExpand}
                disabled={loadingTranscript}
                aria-expanded={expanded}
                aria-label={expanded ? `Hide transcript of ${session.name}` : `Preview transcript of ${session.name}`}
              >
                {loadingTranscript ? <CircularProgress size={16} /> : expanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <IconButton size="small" aria-label={`Actions for ${session.name}`} onClick={onMenu}>
            <MoreHoriz fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 2, pl: { sm: 8 }, display: 'grid', gap: 1.5 }}>
          {session.summary && (
            <Box>
              <Typography variant="overline" color="text.secondary">Summary</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto' }}>
                {session.summary}
              </Typography>
            </Box>
          )}
          <Box>
            <Typography variant="overline" color="text.secondary">
              {session.source === 'fathom' || session.source === 'recall'
                ? 'Transcript'
                : transcript?.contentType === 'paste' ? 'Pasted content' : 'Parsed content'}
            </Typography>
            {transcript ? (
              <TranscriptView transcript={transcript} maxHeight={300} />
            ) : loadingTranscript ? (
              <Skeleton variant="rounded" height={120} sx={{ borderRadius: 'var(--q-radius-xl)' }} />
            ) : (
              <Typography variant="body2" color="text.secondary">No transcript stored for this session.</Typography>
            )}
          </Box>
          <Box>
            <Button size="small" onClick={onOpen}>Open session</Button>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

export default memo(SessionRow);
